import { getDateCellStyle } from '@/common/utils/datePickerUtils';
import {
    ActionIcon,
    Box,
    Button,
    Group,
    Popover,
    PopoverDropdown,
    PopoverTarget,
    Text,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { formatDateWithRelative, isAfterDate, isBeforeDate } from '@tickitacka/shared';
import {
    IconCalendarEvent,
    IconChevronLeft,
    IconChevronRight,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DatePickerSelectorProps {
    /**
     * 현재 선택된 날짜
     */
    selectedDate: Date;

    /**
     * 버튼에 표시할 아이콘이나 컴포넌트
     */
    actionIcon?: React.ReactNode;

    /**
     * 날짜 변경 시 호출되는 함수
     */
    onDateChange: (date: Date) => void;

    /**
     * 날짜 선택 컴포넌트가 Portal 내부에 렌더링되어야 하는지 여부 - false일 경우, 날짜 선택해도 셀렉터의 부모 컴포넌트가 닫히지 않음
     * @default true
     */
    withinPortal?: boolean;

    /**
     * 편집 가능 여부
     * @default true
     */
    editable?: boolean;

    /**
     * 선택된 날짜를 표시할지 여부
     * @default true
     */
    showSelectedDate?: boolean;

    /**
     * 오늘 버튼을 표시할지 여부
     * @default true
     */
    showTodayButton?: boolean;

    /**
     * 이전/다음 날짜 이동 버튼을 표시할지 여부
     * @default false
     */
    showNavigationButtons?: boolean;

    /**
     * 날짜 포맷 옵션
     * @default { year: 'numeric', month: 'long' }
     */
    dateFormatOptions?: Intl.DateTimeFormatOptions;

    /**
     * 오늘 버튼 텍스트
     * @default '오늘' ('date_picker.today')
     */
    todayButtonText?: string;

    /**
     * 캘린더 아이콘 크기
     * @default 16
     */
    calendarIconSize?: number;

    /**
     * 화살표 아이콘 크기
     * @default 10
     */
    arrowIconSize?: number;

    /**
     * 이전/다음 버튼 화살표 아이콘 크기
     * @default 14
     */
    navigationArrowSize?: number;

    /**
     * 버튼 크기
     * @default 'compact-xs'
     */
    buttonSize?: string;

    /**
     * 버튼 radius
     * @default 'md'
     */
    buttonRadius?: string;

    /**
     * 버튼 variant
     * @default 'default'
     */
    buttonVariant?: string;

    /**
     * 추가 CSS 클래스
     */
    className?: string;
}

/**
 * 날짜 선택기 컴포넌트
 *
 * 날짜를 선택하고 오늘 날짜로 이동할 수 있는 버튼을 제공합니다.
 */
export function DatePickerSelector({
    selectedDate,
    actionIcon,
    onDateChange,
    withinPortal = true,
    editable = true,
    showSelectedDate = true,
    showTodayButton = true,
    showNavigationButtons = false,
    dateFormatOptions = { year: 'numeric', month: 'long' },
    todayButtonText,
    calendarIconSize = 20,
    arrowIconSize = 10,
    navigationArrowSize = 14,
    buttonSize = 'compact-xs',
    buttonRadius = 'md',
    buttonVariant = 'default',
    className,
}: DatePickerSelectorProps) {
    const { t } = useTranslation();
    const [datePickerOpened, setDatePickerOpened] = useState(false);

    // 오늘 날짜로 이동하는 함수
    const goToday = () => {
        onDateChange(new Date());
    };

    return (
        <Group justify="center" gap="sm" className={className}>
            {showSelectedDate && (
                <Text fw={600} size="md">
                    {formatDateWithRelative(selectedDate, dateFormatOptions)}
                </Text>
            )}

            {/* Date Picker */}
            <Popover
                opened={datePickerOpened}
                onChange={setDatePickerOpened}
                withinPortal={withinPortal}
            >
                <PopoverTarget>
                    {actionIcon ? (
                        <Box
                            onClick={() =>
                                editable && setDatePickerOpened(true)
                            }
                            style={{
                                cursor: 'pointer',
                            }}
                        >
                            {actionIcon}
                        </Box>
                    ) : (
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="md"
                            onClick={() =>
                                editable && setDatePickerOpened(true)
                            }
                        >
                            <IconCalendarEvent size={calendarIconSize} />
                        </ActionIcon>
                    )}
                </PopoverTarget>
                <PopoverDropdown>
                    <DatePicker
                        firstDayOfWeek={0}
                        value={selectedDate}
                        onChange={(date: Date) => {
                            onDateChange(date || new Date());
                            setDatePickerOpened(false);
                        }}
                        renderDay={date => {
                            // const today = new Date();
                            // const isToday =
                            //     date.getDate() === today.getDate() &&
                            //     date.getMonth() === today.getMonth() &&
                            //     date.getFullYear() === today.getFullYear();

                            // return (
                            //     <div
                            //         style={{
                            //             ...(isToday
                            //                 ? {
                            //                       fontWeight: 'bold',
                            //                   }
                            //                 : {}),
                            //         }}
                            //     >
                            //         {date.getDate()}
                            //     </div>
                            // );

                            const styleObj = getDateCellStyle(
                                date,
                                selectedDate
                            );

                            return <div style={styleObj}>{date.getDate()}</div>;
                        }}
                    />
                </PopoverDropdown>
            </Popover>

            {showNavigationButtons && (
                <Group gap={2}>
                    <ActionIcon
                        color="gray"
                        variant="subtle"
                        onClick={() =>
                            onDateChange(
                                new Date(selectedDate.getTime() - 86400000)
                            )
                        }
                    >
                        <IconChevronLeft
                            stroke={3}
                            size={navigationArrowSize}
                        />
                    </ActionIcon>
                    <ActionIcon
                        color="gray"
                        variant="subtle"
                        onClick={() =>
                            onDateChange(
                                new Date(selectedDate.getTime() + 86400000)
                            )
                        }
                    >
                        <IconChevronRight
                            stroke={3}
                            size={navigationArrowSize}
                        />
                    </ActionIcon>
                </Group>
            )}

            {showTodayButton && (
                <Button
                    variant={buttonVariant}
                    onClick={goToday}
                    size={buttonSize}
                    radius={buttonRadius}
                    style={{
                        visibility:
                            isBeforeDate(selectedDate, new Date()) ||
                            isAfterDate(selectedDate, new Date())
                                ? 'visible'
                                : 'hidden',
                    }}
                >
                    <Group gap={4}>
                        {isAfterDate(selectedDate, new Date()) && (
                            <IconChevronLeft
                                stroke={2}
                                size={navigationArrowSize}
                            />
                        )}
                        {todayButtonText ?? t('date_picker.today')}
                        {isBeforeDate(selectedDate, new Date()) && (
                            <IconChevronRight
                                stroke={2}
                                size={navigationArrowSize}
                            />
                        )}
                    </Group>
                </Button>
            )}
        </Group>
    );
}
